import React from 'react'
import { cn } from '../../utils/cn'
import { Button } from './Button'
import { useTouchGestures } from '../../hooks/useTouchGestures'

export interface ServiceCardProps {
  title: string
  description: string
  pricing: string
  ctaText: string
  icon: React.ReactNode
  features: string[]
  serviceType: 'hr' | 'compliance' | 'trade' | 'intelligence'
  isComingSoon?: boolean
  onClick?: () => void
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  className?: string
}

const serviceColorClasses = {
  hr: 'service-hr',
  compliance: 'service-compliance',
  trade: 'service-trade',
  intelligence: 'service-intelligence',
}

const serviceAccentClasses = {
  hr: 'border-hr-500 bg-hr-50',
  compliance: 'border-compliance-500 bg-compliance-50',
  trade: 'border-trade-500 bg-trade-50',
  intelligence: 'border-intelligence-500 bg-intelligence-50',
}

const serviceButtonClasses = {
  hr: 'bg-hr-600 hover:bg-hr-700',
  compliance: 'bg-compliance-600 hover:bg-compliance-700',
  trade: 'bg-trade-600 hover:bg-trade-700',
  intelligence: 'bg-intelligence-600 hover:bg-intelligence-700',
}

export function ServiceCard({
  title,
  description,
  pricing,
  ctaText,
  icon,
  features,
  serviceType,
  isComingSoon = false,
  onClick,
  onSwipeLeft,
  onSwipeRight,
  className,
}: ServiceCardProps) {
  const cardRef = useTouchGestures<HTMLDivElement>({
    onTap: onClick,
    onSwipe: (gesture) => {
      if (gesture.direction === 'left' && onSwipeLeft) {
        onSwipeLeft()
      } else if (gesture.direction === 'right' && onSwipeRight) {
        onSwipeRight()
      }
    },
    minSwipeDistance: 50,
  })

  return (
    <div
      ref={cardRef}
      className={cn(
        // Base card styles
        'relative bg-white rounded-xl shadow-card p-card',
        'transition-all duration-300 ease-out',
        'hover:shadow-card-hover hover:-translate-y-1',
        'focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2',
        // Touch-friendly
        'touch-manipulation cursor-pointer',
        'active:scale-[0.98] active:shadow-sm',
        // Service-specific styling
        serviceColorClasses[serviceType],
        className
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault()
          onClick()
        }
      }}
      aria-label={`Learn more about ${title}`}
    >
      {/* Coming Soon Badge */}
      {isComingSoon && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Coming Soon
          </span>
        </div>
      )}

      {/* Icon Header */}
      <div className={cn(
        'flex items-center justify-center w-16 h-16 rounded-lg mb-6',
        serviceAccentClasses[serviceType]
      )}>
        <div className="w-8 h-8 text-current">
          {icon}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        <div>
          <h3 className="text-card-title font-semibold text-gray-900 mb-2">
            {title}
          </h3>
          <p className="text-body text-gray-600 line-clamp-3">
            {description}
          </p>
        </div>

        {/* Pricing */}
        <div className="py-2">
          <p className="text-lg font-semibold service-accent">
            {pricing}
          </p>
        </div>

        {/* Features */}
        <ul className="space-y-2">
          {features.slice(0, 3).map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0 service-accent"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="line-clamp-2">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <div className="pt-4">
          <Button
            className={cn(
              'w-full text-white',
              serviceButtonClasses[serviceType]
            )}
            disabled={isComingSoon}
            touchFriendly
            onClick={(e) => {
              e.stopPropagation()
              if (!isComingSoon && onClick) {
                onClick()
              }
            }}
          >
            {isComingSoon ? 'Coming Soon' : ctaText}
          </Button>
        </div>
      </div>

      {/* Swipe indicators for mobile */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-gray-200 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 md:hidden" />
      <div className="absolute inset-y-0 right-0 w-1 bg-gradient-to-b from-transparent via-gray-200 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 md:hidden" />
    </div>
  )
}

// Service card grid container with responsive layout
interface ServiceCardGridProps {
  children: React.ReactNode
  className?: string
}

export function ServiceCardGrid({ children, className }: ServiceCardGridProps) {
  return (
    <div
      className={cn(
        // Mobile: Single column with horizontal scroll
        'flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory',
        'md:grid md:grid-cols-2 md:overflow-visible md:pb-0 md:snap-none',
        'lg:grid-cols-3',
        'xl:grid-cols-4',
        // Hide scrollbar on mobile
        'scrollbar-hide',
        className
      )}
    >
      {React.Children.map(children, (child) => (
        <div className="flex-shrink-0 w-80 snap-center md:w-auto md:snap-align-none">
          {child}
        </div>
      ))}
    </div>
  )
}